/**
 * Loudness safety limits shared by the matcher (backend) and the run
 * confirmation (renderer). Deliberately dependency-free — the renderer
 * imports this module directly, so a `require('path')` here would break the
 * webpack build.
 */

/**
 * EBU R128's ABSOLUTE GATING THRESHOLD, and therefore ffmpeg's reporting
 * floor. Blocks below it are excluded from the integration, so a file with
 * nothing above the gate reports exactly this value — verified against
 * ffmpeg: a 1kHz tone at -49dB gain, the same tone at -90dB gain, and pure
 * digital silence ALL report -70.0, while -48dB reports a real -69.1.
 *
 * So -70.0 is not a loudness, it's "unmeasurable": the true value could be
 * -70, -95, or silence. A gain derived from it is fiction, which is why
 * these files are ALWAYS skipped (no override).
 */
const EBUR128_FLOOR_LUFS = -70;

/**
 * Boost beyond which a match is flagged as damaging. Gain lifts the noise
 * floor with the music: a 16-bit file's dither sits near -96dBFS, so a
 * +32dB boost puts it around -64dBFS — audible hiss — and transients can
 * clip. Unlike the floor above this is a QUALITY warning, not a correctness
 * one: the math is valid, the source is just poor, so the user can override
 * it per run ("Match anyway").
 *
 * For scale, at a -9 LUFS target this only flags sources below -39 LUFS.
 * Typical material: masters -5..-12, older CDs -12..-18, quiet vinyl rips
 * -20..-28, badly recorded -30..-40.
 */
const MAX_MATCH_GAIN_DB = 30;

/**
 * True-peak ceiling for produced files, in dBFS.
 *
 * Loudness matching applies a flat gain, and ffmpeg's `volume` filter has no
 * headroom: boosting a track whose peaks already sit near 0 dBFS FLAT-TOPS
 * the waveform. Measured on a real master (peaks -0.1 dBFS, -3.8 LUFS) a
 * routine +11dB match hard-clipped 82% of its samples while still reporting
 * the requested LUFS — the file looked matched and sounded destroyed.
 *
 * -1 dBFS (rather than 0) leaves room for inter-sample peaks that reappear on
 * D/A conversion and lossy transcodes.
 */
const PEAK_CEILING_DBFS = -1;

/**
 * How much a file can be boosted before its true peak hits the ceiling.
 * Returns Infinity when the peak is unknown (nothing to protect against —
 * behave exactly as before rather than guessing a limit).
 */
function headroomDb(truePeak) {
    if (typeof truePeak !== 'number' || isNaN(truePeak)) return Infinity;
    return PEAK_CEILING_DBFS - truePeak;
}

/**
 * The gain we may actually apply: the requested amount, reduced to the file's
 * headroom when boosting. Attenuation is never limited (it only lowers peaks).
 */
function cappedGainDb(requestedGainDb, truePeak) {
    if (typeof requestedGainDb !== 'number' || isNaN(requestedGainDb)) return requestedGainDb;
    if (requestedGainDb <= 0) return requestedGainDb;
    return Math.min(requestedGainDb, Math.max(0, headroomDb(truePeak)));
}

/** True when a measurement is pinned at the gate floor (see above). */
function isUnmeasurableLoudness(loudness) {
    return typeof loudness === 'number' && loudness <= EBUR128_FLOOR_LUFS;
}

/**
 * dB of boost a match would apply (negative = attenuation, always safe).
 * Returns null when either side isn't a usable number.
 */
function matchGainDb(integratedLoudness, targetLoudness) {
    const target = Number(targetLoudness);
    if (typeof integratedLoudness !== 'number' || isNaN(integratedLoudness) || isNaN(target)) return null;
    return target - integratedLoudness;
}

/** True when the boost exceeds the damage threshold (see MAX_MATCH_GAIN_DB). */
function needsExtremeBoost(integratedLoudness, targetLoudness) {
    const gain = matchGainDb(integratedLoudness, targetLoudness);
    return gain !== null && gain > MAX_MATCH_GAIN_DB;
}

module.exports = {
    EBUR128_FLOOR_LUFS,
    MAX_MATCH_GAIN_DB,
    PEAK_CEILING_DBFS,
    isUnmeasurableLoudness,
    matchGainDb,
    needsExtremeBoost,
    headroomDb,
    cappedGainDb,
};
